// src/lib/scanCode.js
// ───────────────────────────────────────────────────────────────────────────
// 🔳 Contrato ÚNICO de códigos QR del sistema (PLAN-TERMINAL-OPERADOR §9.3 Pilar 2)
//
// ÚNICA fuente de GENERACIÓN y PARSEO de lo que va dentro de un QR ("reusar,
// no duplicar"). Puro y unit-testeable. Contrato v1 (claves cortas → QR menos
// denso → lectura más fácil en etiquetas térmicas pequeñas y con cámara):
//
//   { "v": 1, "t": "sheet",    "j": "J-1123", "id": "<sheetDocId>", "hri": "1123_001P" }
//   { "v": 1, "t": "piece",    "j": "J-1123", "id": "<pieceCode>" }
//   { "v": 1, "t": "artifact", "j": "J-1123", "id": "<artifactId>" }
//
// parseScanInput() además acepta TODOS los payloads legados ya impresos
// ({jobId,sheetDocId} · {jobId,sheetId} · {jobId,pieceCode} · artifact de
// naming.js) y texto plano (hri tecleado o de etiqueta vieja).
// **Nada ya impreso deja de funcionar.**
//
// Consumidores: SheetModal y PiecesInventoryTab (generan), FileFinder y /scan
// (parsean), futuro /terminal (parsea).
// ───────────────────────────────────────────────────────────────────────────
import { normalizeHriSearch } from './sheetNaming.mjs';

export const SCAN_CONTRACT_VERSION = 1;

/** QR de plancha. `hri` viaja para que el dato legible acompañe al ID. */
export function buildSheetScanCode({ jobId, sheetDocId, hri }) {
  return JSON.stringify({ v: 1, t: 'sheet', j: jobId, id: sheetDocId, hri: hri || '' });
}

/** QR de pieza (etiquetas de PiecesInventoryTab / SheetModal). */
export function buildPieceScanCode({ jobId, pieceCode }) {
  return JSON.stringify({ v: 1, t: 'piece', j: jobId, id: pieceCode });
}

/** QR de artefacto/archivo (reemplaza artifactQrPayload de naming.js). */
export function buildArtifactScanCode({ jobId, artifactId }) {
  return JSON.stringify({ v: 1, t: 'artifact', j: jobId, id: artifactId });
}

/**
 * Parsea CUALQUIER cosa que entre por un input de escaneo (QR v1, QR legado,
 * texto tecleado) y la normaliza a:
 *     { kind: 'sheet'|'piece'|'artifact'|'text'|'unknown', jobId, id, hri }
 *
 * - kind 'sheet'/'piece'/'artifact' → trae jobId + id (lookup directo por ID).
 * - kind 'text'   → no era JSON: es un hri tecleado/escaneado de etiqueta vieja.
 *                   `hri` trae el texto normalizado para `hriSearch` (minúsculas, sin #).
 * - kind 'unknown'→ era JSON pero de un formato que no reconocemos (error honesto).
 * - null          → entrada vacía.
 */
export function parseScanInput(raw) {
  const text = String(raw == null ? '' : raw).trim();
  if (!text) return null;

  let data = null;
  try { data = JSON.parse(text); } catch (_) { /* no es JSON → texto */ }

  if (data === null || typeof data !== 'object') {
    return { kind: 'text', jobId: null, id: null, hri: normalizeHriSearch(text), raw: text };
  }

  // ── Contrato v1 ────────────────────────────────────────────────────────────
  if (data.v === 1 && data.t && data.j && data.id) {
    const kind = data.t === 'sheet' || data.t === 'piece' || data.t === 'artifact' ? data.t : 'unknown';
    if (kind !== 'unknown') {
      return { kind, jobId: data.j, id: data.id, hri: data.hri || '', raw: text };
    }
  }

  // ── Legados (todo lo ya impreso sigue funcionando) ─────────────────────────
  // QR de plancha viejo (FileFinder original): { jobId, sheetDocId }
  if (data.jobId && data.sheetDocId) {
    return { kind: 'sheet', jobId: data.jobId, id: data.sheetDocId, hri: '', raw: text };
  }
  // QR de plancha que genera HOY SheetModal (D4): { jobId, sheetId }
  if (data.jobId && data.sheetId) {
    return { kind: 'sheet', jobId: data.jobId, id: data.sheetId, hri: '', raw: text };
  }
  // QR de pieza (PiecesInventoryTab / SheetModal): { jobId, pieceCode }
  if (data.jobId && data.pieceCode) {
    return { kind: 'piece', jobId: data.jobId, id: data.pieceCode, hri: '', raw: text };
  }
  // QR de artefacto (naming.js): { t:'artifact', jobId, artifactId, ... }
  if (data.t === 'artifact' && (data.artifactId || data.jobId)) {
    return { kind: 'artifact', jobId: data.jobId || null, id: data.artifactId || null, hri: '', raw: text };
  }

  return { kind: 'unknown', jobId: null, id: null, hri: '', raw: text };
}
